import React, { useState } from 'react';
import {
  X,
  Mail,
  Lock,
  AlertCircle,
  ArrowLeft,
  Gift,
  Eye,
  EyeOff,
  ChevronRight,
  Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import LanguageSwitcher from './LanguageSwitcher';
import AuthBackground from './AuthBackground';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthView = 'signIn' | 'signUp' | 'forgotPassword';

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [authView, setAuthView] = useState<AuthView>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { signIn, signUp, resetPasswordForEmail } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (authView === 'signIn') {
        const { error: signInError } = await signIn(email, password);

        if (signInError) {
          throw signInError;
        }

        sessionStorage.removeItem('marketLoadingShown');
        onClose();
        return;
      }

      if (authView === 'signUp') {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        const { error: signUpError } = await signUp(email, password, referralCode);

        if (signUpError) {
          throw signUpError;
        }

        sessionStorage.removeItem('marketLoadingShown');
        onClose();
        return;
      }

      const { error: resetError } = await resetPasswordForEmail(email);
      if (resetError) {
        throw resetError;
      }

      setResetEmailSent(true);
    } catch (authError: any) {
      setError(authError.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setReferralCode('');
    setError('');
    setResetEmailSent(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAuthView('signIn');
  };

  const switchView = (view: AuthView) => {
    setAuthView(view);
    setError('');
    setResetEmailSent(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const cardClass =
    'bg-gradient-to-br from-violet-500/18 via-purple-500/14 to-fuchsia-500/14 backdrop-blur-md rounded-2xl border-2 border-violet-500/55 p-8 shadow-2xl shadow-slate-950/35';
  const inputClass =
    'purple-form-field w-full rounded-xl border border-violet-500/55 bg-violet-950/55 py-3 text-white placeholder:text-slate-400 shadow-inner shadow-black/30 transition-all focus:outline-none focus:ring-2 focus:ring-violet-400/70 focus:border-violet-300/75';
  const primaryButtonClass =
    'w-full rounded-xl border border-blue-400/30 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 py-4 font-semibold text-white shadow-xl shadow-blue-500/50 transition-all duration-300 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-600 disabled:from-slate-700 disabled:to-slate-800';

  const heading =
    authView === 'signIn'
      ? t('auth.welcomeBack')
      : authView === 'signUp'
        ? t('auth.joinPlatform')
        : resetEmailSent
          ? t('auth.checkYourEmail')
          : t('auth.resetYourPassword');

  const subheading =
    authView === 'signIn'
      ? t('auth.signInToAccess')
      : authView === 'signUp'
        ? t('auth.createYourAccount')
        : resetEmailSent
          ? t('auth.weveSentPasswordReset')
          : t('auth.wellSendYouLink');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/72">
      <AuthBackground />

      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div className="relative z-20 w-full max-w-md">
          <div className="mb-10 text-center">
            <div className="mb-4 flex items-center justify-end gap-3">
              <LanguageSwitcher variant="auth" />
              <button
                onClick={handleClose}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-violet-500/35 bg-gradient-to-br from-violet-500/16 via-purple-500/12 to-fuchsia-500/14 text-slate-300 transition-all duration-300 hover:border-violet-400/55 hover:text-white"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <h1 className="mb-2 bg-gradient-to-r from-blue-300 via-purple-300 to-violet-300 bg-clip-text text-4xl font-bold text-transparent">
              {heading}
            </h1>
            <p className="mt-2 text-lg text-slate-300">{subheading}</p>
          </div>

          <div className={cardClass}>
            {error && (
              <div className="mb-6 flex items-center gap-3 rounded-xl border-2 border-red-600 bg-red-600/30 p-4 shadow-lg shadow-red-500/20">
                <AlertCircle size={20} className="shrink-0 text-red-400" />
                <span className="text-red-400">{error}</span>
              </div>
            )}

            {authView === 'signIn' && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm text-slate-400">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.enterYourEmail')}
                      className={`${inputClass} pl-10 pr-4`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm text-slate-400">{t('auth.password')}</label>
                    <button
                      type="button"
                      onClick={() => switchView('forgotPassword')}
                      className="text-xs text-blue-400 transition-colors hover:text-blue-300"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.enterYourPassword')}
                      className={`${inputClass} pl-10 pr-10`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`${primaryButtonClass} mt-2 flex items-center justify-center gap-2`}
                >
                  {loading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      {t('auth.signIn')}
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>

                <div className="text-center">
                  <p className="text-sm text-slate-300">
                    {t('auth.dontHaveAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => switchView('signUp')}
                      className="font-medium text-blue-400 underline transition-colors hover:text-blue-300"
                    >
                      {t('auth.createAccount')}
                    </button>
                  </p>
                </div>
              </form>
            )}

            {authView === 'signUp' && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="mb-4 text-xl font-semibold text-white">{t('auth.createYourAccountStep')}</h2>

                <div>
                  <label className="mb-2 block text-sm text-slate-400">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.enterYourEmail')}
                      className={`${inputClass} pl-10 pr-4`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm text-slate-400">{t('auth.password')}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.createAPassword')}
                      className={`${inputClass} pl-10 pr-10`}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="ml-1 mt-1 text-xs text-slate-500">{t('auth.mustBeAtLeast6Characters')}</p>
                </div>

                <div>
                  <label className="mb-2 block text-sm text-slate-400">{t('auth.confirmPassword')}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('auth.confirmYourPasswordPlaceholder')}
                      className={`${inputClass} pl-10 pr-10`}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm text-slate-400">
                    {t('auth.referralCode')} <span className="text-slate-500">({t('common.optional')})</span>
                  </label>
                  <div className="relative">
                    <Gift size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      placeholder={t('auth.enterReferralCode')}
                      className={`${inputClass} pl-10 pr-4`}
                    />
                  </div>
                  <p className="ml-1 mt-1 text-xs text-slate-500">{t('auth.getTradingBonuses')}</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`${primaryButtonClass} flex items-center justify-center gap-2`}
                >
                  {loading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      {t('auth.createAccount')}
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>

                <div className="text-center">
                  <p className="text-slate-300">
                    {t('auth.alreadyHaveAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => switchView('signIn')}
                      className="font-medium text-blue-400 underline transition-colors hover:text-blue-300"
                    >
                      {t('auth.signIn')}
                    </button>
                  </p>
                </div>
              </form>
            )}

            {authView === 'forgotPassword' && !resetEmailSent && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <p className="mb-6 text-slate-300">{t('auth.wellSendYouLink')}</p>
                  <label className="mb-2 block text-sm text-slate-400">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.enterYourEmail')}
                      className={`${inputClass} pl-10 pr-4`}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`${primaryButtonClass} flex items-center justify-center gap-2`}
                >
                  {loading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      {t('auth.resetPassword')}
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => switchView('signIn')}
                  className="flex w-full items-center justify-center gap-2 py-2 text-slate-400 transition-colors hover:text-white"
                >
                  <ArrowLeft size={16} />
                  {t('auth.backToSignIn')}
                </button>
              </form>
            )}

            {authView === 'forgotPassword' && resetEmailSent && (
              <div className="py-2 text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 shadow-lg shadow-blue-500/25">
                  <Mail size={32} className="text-white" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white">{t('auth.checkYourEmail')}</h3>
                <p className="mb-6 text-slate-300">
                  {t('auth.weveSentPasswordReset')}{' '}
                  <span className="font-medium text-white">{email}</span>
                </p>

                <button
                  type="button"
                  onClick={() => switchView('signIn')}
                  className={`${primaryButtonClass} inline-flex items-center justify-center gap-2 px-6`}
                >
                  {t('auth.returnToSignIn')}
                </button>

                <p className="mt-4 text-sm text-slate-400">
                  {t('auth.didntReceiveEmail')}{' '}
                  <button
                    type="button"
                    onClick={() => setResetEmailSent(false)}
                    className="text-blue-400 transition-colors hover:text-blue-300"
                  >
                    {t('auth.tryAgain')}
                  </button>
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-purple-500/8 to-fuchsia-500/10 px-4 py-3 text-center shadow-lg shadow-slate-950/20">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-300">
              <Shield size={16} />
              <span>{t('auth.secureConnection')}</span>
            </div>
          </div>

          <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full border border-blue-500/20" />
          <div className="absolute -left-5 -top-5 h-20 w-20 rounded-full border border-purple-500/20" />
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
